// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserContext } from '~/dashboard/context';
import type { ThresholdRule } from 'flow/service/rules';
import {
  selectActions,
  selectNodeLineageForAnchorNode,
  selectPublicVersion,
  selectRuleBuilderActiveSubEditor,
  selectRuleBuilderPendingGroupInfo,
  selectThresholdById,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import RuleTemplate from '../RuleTemplate';
import LHSWrapper from './LHSWrapper';
import OperatorWrapper from './OperatorWrapper';
import RHSWrapper from './RHS/RHSWrapper';
import IsolatedTextEditor from './RHS/IsolatedTextEditor';
import { getThresholdType, isThresholdUsingNullOperator, populateThresholdData } from 'flow/service/thresholds';
import { canSwitchToDynamicComparison, shouldRenderOperatorDropdown } from './helpers';
import { getActionDataType } from 'flow/service/actions';
import { RuleBuilderContext } from '../../context';
import { RuleBuilderDragItem } from '../../types';
import { useDrag } from 'react-dnd';
import { StyledThresholdContent } from '../styles';
import ThresholdResult from './RuleExplainability/ThresholdResult';
import { ThresholdContext } from './context';
import useThresholdSimulatorParams from 'flow/api/useThresholdSimulatorParams';
import { formatThresholdSimParams } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/ThresholdSimulatorModal/helpers';
import { setThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/slice';
import MLAWrapper from './MLAWrapper';
import { RuleBuilderSubEditorType } from 'flow/store/engine/types/utils';
import { ContainerType } from '../../../types';
import { isAggregationServiceAction } from '~/dashboard/views/workflows/service/services';
import { StyledPre } from 'flow/editor/styles';
import AggregationsWrapper from './AggregationsWrapper';
import { selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { getHasPendingCloseParenthesis } from '../RuleTemplate/helpers';
import DynamicSwitchButton from './DynamicSwitchButton';
import { isEditable } from '../../helpers';
import { setRuleBuilderIsDragging } from 'flow/store/redux/slice';
import { DiffContext } from 'flow/editor/components/DiffViewSplit/DiffContext';
import type { PublicId } from 'flow/store/engine/types';
import { Operation } from 'flow/service/changelog/types';

export type ThresholdProps = {
  rule: ThresholdRule;
};

const Threshold = ({ rule }: ThresholdProps) => {
  const {
    sourceAction,
    containerType,
    editMode,
    ruleTree,
  } = useContext(RuleBuilderContext);
  const { featureFlags } = useContext(UserContext);

  const dispatch = useAppDispatch();
  const threshold = useAppSelector(selectThresholdById(rule.id));
  const actions = useAppSelector(selectActions);
  const workflowVersion = useAppSelector(selectPublicVersion);
  const {
    reasonCodeGroups, services, workflow, universalAttributes,
  } = useAppSelector(selectWorkflowProps);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const pendingGroupInfo = useAppSelector(selectRuleBuilderPendingGroupInfo);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);

  const populatedThreshold = populateThresholdData({
    actions, reasonCodeGroups, services, threshold, universalAttributes,
  });

  const [isHovered, setIsHovered] = useState(false);
  const workflowDiff = useContext(DiffContext);

  function getThresholdDiffStatus(id: PublicId): Operation | null {
    const { added, modified, removed } = workflowDiff;
    if (added.has(id)) {
      return Operation.Add;
    } if (modified.has(id)) {
      return Operation.Modify;
    } if (removed.has(id)) {
      return Operation.Remove;
    }
    return null;
  }

  const diffStatus = getThresholdDiffStatus(threshold.publicId);

  /**
   * This flag keeps track of whether the threshold simulator params have been initialized.
   * This prevents the useEffect from firing multiple times when the query refetches data,
   * similar to the isDataInitialized pattern in WorkflowEditorView.
   */
  const isThresholdSimParamsInitialized = useRef<boolean>(false);
  const lastInitializedRuleId = useRef<string | null>(null);

  const thresholdSimQueryResponse = useThresholdSimulatorParams({
    publicId: threshold.publicId,
    workflowVersion,
    enabled: featureFlags.thresholdSimulator,
  });

  const formattedParams = useMemo(
    () => formatThresholdSimParams(
      rule.id,
      {
        data: thresholdSimQueryResponse.data,
        error: thresholdSimQueryResponse.error,
        isLoading: thresholdSimQueryResponse.isLoading,
      }),
    [
      rule.id,
      thresholdSimQueryResponse.data,
      thresholdSimQueryResponse.error,
      thresholdSimQueryResponse.isLoading,
    ],
  );

  useEffect(() => {
    if (!featureFlags.thresholdSimulator) {
      return;
    }

    // Reset initialization flag if we're dealing with a different rule
    if (lastInitializedRuleId.current !== rule.id) {
      isThresholdSimParamsInitialized.current = false;
      lastInitializedRuleId.current = rule.id;
    }

    if (!isThresholdSimParamsInitialized.current) {
      dispatch(setThresholdSimParamsByPublicId(formattedParams));
      isThresholdSimParamsInitialized.current = true;
    }
  }, [dispatch, featureFlags.thresholdSimulator, rule.id, formattedParams]);

  /* Only thresholds support dragging at this time */
  const [, dragSource, dragPreview] = useDrag(() => ({
    type: `${sourceAction.publicId}-rule`,
    item: () => {
      dispatch(setRuleBuilderIsDragging(true));
      return { rule, sourceActionId: sourceAction.publicId } satisfies RuleBuilderDragItem;
    },
    end: () => dispatch(setRuleBuilderIsDragging(false)),
  }), [rule, sourceAction]);

  const lhsDataType = getActionDataType(populatedThreshold.left);

  const isDynamic = getThresholdType(populatedThreshold) === 'dynamic';

  const isAggregationService = isAggregationServiceAction(populatedThreshold.left.action);
  const shouldRenderOperator = shouldRenderOperatorDropdown(
    populatedThreshold,
    lhsDataType,
    isDynamic,
  );
  const shouldShowAggregationIsLabel = isAggregationService
    && populatedThreshold.left.attribute;

  const operatorIsNullOrNotNull = !isDynamic
    && isThresholdUsingNullOperator({
      leftDataType: lhsDataType,
      threshold: populatedThreshold,
      supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
    });

  const thresholdContext = useMemo(() => ({
    isDynamic,
    lhsDataType,
    populatedThreshold,
    rule,
  }), [isDynamic, lhsDataType, populatedThreshold, rule]);

  const showIsolatedEditor = activeSubEditor.thresholdId === threshold.publicId;

  const canSwitchToDynamic = useMemo(() => canSwitchToDynamicComparison({
    populatedThreshold,
    nodeLineage,
    actions,
    workflow,
    services,
    lhsDataType,
    universalAttributes,
  }), [
    populatedThreshold, nodeLineage, actions, workflow, services, lhsDataType, universalAttributes,
  ]);

  const hasPendingCloseParenthesis = useMemo(() => (
    getHasPendingCloseParenthesis({
      rule,
      ruleTree,
      pendingGroupInfo,
      groupPosition: undefined, // Thresholds don't have group positions
    })
  ), [rule, ruleTree, pendingGroupInfo]);

  const canRenderDynamicSwitch = (canSwitchToDynamic || isDynamic)
    && !hasPendingCloseParenthesis && isEditable(editMode);
  const canShowDynamicSwitch = isHovered && canRenderDynamicSwitch;

  function renderSubEditor() {
    switch (activeSubEditor.type) {
      case (RuleBuilderSubEditorType.ISOLATED_EDITOR): {
        return <IsolatedTextEditor />;
      }
      case (RuleBuilderSubEditorType.MLA_EDITOR): {
        return <MLAWrapper />;
      }
      case (RuleBuilderSubEditorType.AGGREGATIONS_EDITOR): {
        return <AggregationsWrapper />;
      }
      default: return null;
    }
  }
  const shouldRenderRuleExplainabilityContent = featureFlags?.ruleExplainabilityPhase > 0
    && ![ContainerType.TOOLTIP, ContainerType.ISLAND].includes(containerType)
    && thresholdOutputResults;

  const dragOpts = isEditable(editMode) ? { dragSource, dragPreview } : undefined;

  return (
    <ThresholdContext.Provider value={thresholdContext}>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <RuleTemplate
          rule={rule}
          dragOpts={dragOpts}
          endContent={
            shouldRenderRuleExplainabilityContent
              ? <ThresholdResult />
              : undefined
          }
        >
          <StyledThresholdContent isPreview={threshold.isPreview} diffStatus={diffStatus}>
            <LHSWrapper />
            {shouldShowAggregationIsLabel && <StyledPre>is</StyledPre>}
            {shouldRenderOperator && (
              <OperatorWrapper
                operatorIsNullOrNotNull={operatorIsNullOrNotNull}
              />
            )}
            {!operatorIsNullOrNotNull && (
              <RHSWrapper />
            )}
            {canShowDynamicSwitch && <DynamicSwitchButton />}
          </StyledThresholdContent>
        </RuleTemplate>
      </div>
      {showIsolatedEditor && renderSubEditor()}
    </ThresholdContext.Provider>
  );
};

export default Threshold;
